import React, { useEffect, useRef, useState } from 'react'

const processSteps = [
  {
    number: "01",
    title: "Client Discovery & Goal Understanding",
    icon: "🤝",
    points: [
      "In-depth discussion to understand personal aspirations and life direction",
      "Identifying current challenges related to career, relationships or well-being",
      "Understanding lifestyle, living environment and day-to-day energy patterns",
      "Clarifying expectations and desired outcomes from the advanced consultation",
    ],
  },
  {
    number: "02",
    title: "Collection of Personal Inputs & Concerns",
    icon: "📝",
    points: [
      "Gathering information about recurring life patterns and personal blockages",
      "Understanding significant past events that shaped current circumstances",
      "Identifying areas requiring transformation — home, workspace or mindset",
      "Collecting date of birth, sun sign and any previous astrological data",
    ],
  },
  {
    number: "03",
    title: "Palmistry Analysis — Reading Your Life Map",
    icon: "🖐️",
    points: [
      "Detailed examination of the major lines — Life, Head, Heart and Fate",
      "Analysis of hand shape, finger lengths and mount formations",
      "Identification of dominant personality traits, strengths and life patterns",
      "Reading of minor lines for insights into health, travel, creativity and intuition",
    ],
  },
  {
    number: "04",
    title: "Astrology & Sign-Based Energy Analysis",
    icon: "♈",
    points: [
      "Sun sign, moon sign and rising sign analysis for complete energetic profile",
      "Identification of supportive planetary elements, colors and directions",
      "Understanding astrological strengths, weaknesses and karmic influences",
      "Mapping elemental affinities — Fire, Earth, Air or Water — to space design",
    ],
  },
  {
    number: "05",
    title: "Theme Guidance for Home or Workspace",
    icon: "🏠",
    points: [
      "Personalized interior theme suggestions aligned with palm and astrological profile",
      "Direction-wise placement of objects, furniture and decor elements",
      "Color palette recommendations based on planetary and elemental energies",
      "Guidance on textures, materials and spatial arrangements for positive energy flow",
    ],
  },
  {
    number: "06",
    title: "Creating a Space for Growth, Harmony & Success",
    icon: "✨",
    points: [
      "Step-by-step implementation guidance for home or office transformation",
      "Recommendations for symbols, artwork and plants that amplify positive energy",
      "Remedies for neutralizing negative influences in existing spaces",
      "Follow-up support to ensure the space sustains harmony and momentum",
    ],
  },
]

const zodiacSigns = [
  { sign: "♈", name: "Fire", members: "Aries · Leo · Sagittarius", theme: "Bold, Energetic Spaces", color: "#ff6b6b" },
  { sign: "♉", name: "Earth", members: "Taurus · Virgo · Capricorn", theme: "Grounded, Natural Spaces", color: "#b5ead7" },
  { sign: "♊", name: "Air", members: "Gemini · Libra · Aquarius", theme: "Open, Flowing Spaces", color: "#a0c4ff" },
  { sign: "♋", name: "Water", members: "Cancer · Scorpio · Pisces", theme: "Calm, Intuitive Spaces", color: "#c3aed6" },
]

const palmLines = [
  { name: "Life Line", icon: "❤️", desc: "Reveals vitality, energy levels and major life transitions" },
  { name: "Head Line", icon: "🧠", desc: "Reflects intellect, decision-making style and mental strengths" },
  { name: "Heart Line", icon: "💛", desc: "Indicates emotional nature, relationships and love patterns" },
  { name: "Fate Line", icon: "⭐", desc: "Shows career direction, destiny influences and life purpose" },
]

const ProcessCard = ({ step, index }) => {
  const [visible, setVisible] = useState(false)
  const ref = useRef()

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true) },
      { threshold: 0.15 }
    )
    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(40px)',
        transition: `opacity 0.7s ease ${index * 0.1}s, transform 0.7s ease ${index * 0.1}s`,
        background: 'linear-gradient(135deg, rgba(130,94,10,0.08) 0%, rgba(255,255,255,0.95) 100%)',
        border: '1px solid rgba(130,94,10,0.18)',
        borderLeft: '4px solid #825e0a',
        borderRadius: '12px',
        padding: '32px 28px',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'default',
        boxShadow: '0 4px 24px rgba(130,94,10,0.07)',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 12px 40px rgba(130,94,10,0.18)'
        e.currentTarget.style.borderLeftColor = '#c48a14'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = '0 4px 24px rgba(130,94,10,0.07)'
        e.currentTarget.style.borderLeftColor = '#825e0a'
      }}
    >
      <div style={{
        position: 'absolute', top: '-10px', right: '16px',
        fontSize: '90px', fontFamily: '"Playfair Display", serif',
        color: 'rgba(130,94,10,0.06)', fontWeight: '900',
        lineHeight: 1, userSelect: 'none', pointerEvents: 'none',
      }}>
        {step.number}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '20px' }}>
        <div style={{
          width: '52px', height: '52px', borderRadius: '50%',
          background: 'linear-gradient(135deg, #825e0a, #c48a14)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '22px', flexShrink: 0,
          boxShadow: '0 4px 12px rgba(130,94,10,0.3)',
        }}>
          {step.icon}
        </div>
        <div>
          <span style={{
            fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase',
            color: '#c48a14', fontWeight: '700', fontFamily: '"Montserrat", sans-serif',
          }}>
            Step {step.number}
          </span>
          <h3 style={{
            margin: '4px 0 0', fontSize: '18px', fontWeight: '700',
            color: '#2c1a00', fontFamily: '"Playfair Display", serif',
            lineHeight: '1.3',
          }}>
            {step.title}
          </h3>
        </div>
      </div>

      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {step.points.map((point, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <span style={{
              width: '18px', height: '18px', borderRadius: '50%',
              background: 'rgba(130,94,10,0.1)',
              border: '1.5px solid rgba(130,94,10,0.35)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, marginTop: '2px',
            }}>
              <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#825e0a', display: 'block' }} />
            </span>
            <span style={{ fontSize: '14px', color: '#5a3d10', lineHeight: '1.6', fontFamily: '"Lato", sans-serif' }}>
              {point}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

const PalmistryAstro = () => {
  const [headerVisible, setHeaderVisible] = useState(false)
  const [elementsVisible, setElementsVisible] = useState(false)
  const [linesVisible, setLinesVisible] = useState(false)
  const elementsRef = useRef()
  const linesRef = useRef()

  useEffect(() => {
    const t = setTimeout(() => setHeaderVisible(true), 100)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    const obs1 = new IntersectionObserver(([e]) => { if (e.isIntersecting) setElementsVisible(true) }, { threshold: 0.2 })
    const obs2 = new IntersectionObserver(([e]) => { if (e.isIntersecting) setLinesVisible(true) }, { threshold: 0.2 })
    if (elementsRef.current) obs1.observe(elementsRef.current)
    if (linesRef.current) obs2.observe(linesRef.current)
    return () => { obs1.disconnect(); obs2.disconnect() }
  }, [])

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;900&family=Montserrat:wght@400;600;700&family=Lato:wght@300;400;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #fdf8f0; }

        .pa-section { min-height: 100vh; background: #fdf8f0; font-family: 'Lato', sans-serif; overflow: hidden; }

        .pa-hero {
          position: relative;
          background: linear-gradient(160deg, #2c1a00 0%, #4a2e05 40%, #825e0a 100%);
          padding: 80px 24px 100px;
          text-align: center;
          overflow: hidden;
        }

        .mandala-ring {
          position: absolute; border-radius: 50%;
          border: 1px solid rgba(196,138,20,0.12);
          top: 50%; left: 50%; pointer-events: none;
        }
        .spin { animation: rotateSlow 60s linear infinite; }
        .spin-rev { animation: rotateSlowRev 80s linear infinite; }
        @keyframes rotateSlow { from { transform: translate(-50%,-50%) rotate(0deg); } to { transform: translate(-50%,-50%) rotate(360deg); } }
        @keyframes rotateSlowRev { from { transform: translate(-50%,-50%) rotate(0deg); } to { transform: translate(-50%,-50%) rotate(-360deg); } }

        .badge {
          display: inline-block;
          background: rgba(196,138,20,0.15);
          border: 1px solid rgba(196,138,20,0.4);
          color: #f0c060; padding: 6px 18px; border-radius: 40px;
          font-size: 11px; letter-spacing: 3px; text-transform: uppercase;
          font-family: 'Montserrat', sans-serif; font-weight: 700; margin-bottom: 20px;
        }

        .sub-badges { display: flex; justify-content: center; gap: 12px; flex-wrap: wrap; margin-top: 24px; }
        .sub-badge {
          background: rgba(253,248,240,0.08); border: 1px solid rgba(240,192,96,0.25);
          color: rgba(253,248,240,0.7); padding: 6px 16px; border-radius: 30px;
          font-size: 12px; font-family: 'Montserrat', sans-serif; font-weight: 600; letter-spacing: 0.5px;
        }

        .divider-row { display: flex; align-items: center; justify-content: center; gap: 12px; margin-top: 12px; }
        .line-r { height: 1px; width: 60px; background: linear-gradient(to right, transparent, #c48a1488, #c48a14); }
        .line-l { height: 1px; width: 60px; background: linear-gradient(to left, transparent, #c48a1488, #c48a14); }

        .dark-strip {
          background: linear-gradient(160deg, #2c1a00 0%, #4a2e05 60%, #3a2204 100%);
          padding: 72px 24px;
        }

        .elements-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px; max-width: 960px; margin: 40px auto 0;
        }

        .element-card {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(196,138,20,0.18);
          border-radius: 16px; padding: 28px 20px; text-align: center;
          transition: transform 0.3s ease, background 0.3s ease;
        }
        .element-card:hover { transform: translateY(-6px); background: rgba(255,255,255,0.09); }

        .palm-lines-strip {
          background: #fdf8f0;
          padding: 72px 24px;
        }
        .palm-lines-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 20px; max-width: 960px; margin: 40px auto 0;
        }
        .palm-line-card {
          background: linear-gradient(135deg, rgba(130,94,10,0.06), rgba(255,255,255,0.9));
          border: 1px solid rgba(130,94,10,0.15);
          border-top: 3px solid #825e0a;
          border-radius: 12px; padding: 28px 20px; text-align: center;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .palm-line-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 32px rgba(130,94,10,0.15);
        }

        .steps-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 24px; max-width: 1100px; margin: 0 auto; padding: 48px 24px 64px;
        }

        .section-heading { text-align: center; padding: 64px 24px 0; }

        .cta-strip {
          background: linear-gradient(135deg, #825e0a, #4a2e05);
          padding: 56px 24px; text-align: center; position: relative; overflow: hidden;
        }
        .cta-strip::before { content: '✦'; position: absolute; top: 24px; left: 40px; color: rgba(255,255,255,0.06); font-size: 60px; }
        .cta-strip::after  { content: '✦'; position: absolute; bottom: 24px; right: 40px; color: rgba(255,255,255,0.06); font-size: 60px; }

        .cta-btn {
          display: inline-block;
          background: linear-gradient(135deg, #f0c060, #c48a14);
          color: #2c1a00; padding: 16px 44px; border-radius: 50px;
          font-family: 'Montserrat', sans-serif; font-weight: 700; font-size: 15px;
          letter-spacing: 1px; border: none; cursor: pointer;
          box-shadow: 0 8px 28px rgba(196,138,20,0.4);
          transition: transform 0.25s ease, box-shadow 0.25s ease; text-decoration: none;
        }
        .cta-btn:hover { transform: translateY(-3px); box-shadow: 0 14px 36px rgba(196,138,20,0.55); }
      `}</style>

      <section className="pa-section">

        {/* Hero */}
        <div className="pa-hero">
          {[500, 700, 900].map((s, i) => (
            <div key={i} className={`mandala-ring ${i % 2 === 0 ? 'spin' : 'spin-rev'}`} style={{ width: s, height: s }} />
          ))}

          <div style={{
            position: 'relative', zIndex: 2,
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(-30px)',
            transition: 'opacity 0.9s ease, transform 0.9s ease',
          }}>
            <div className="badge">✦ Advanced Consultation ✦</div>

            <h1 style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 'clamp(28px, 5vw, 50px)',
              fontWeight: '900', color: '#fdf8f0',
              lineHeight: '1.2', marginBottom: '16px',
              textShadow: '0 2px 20px rgba(0,0,0,0.3)',
            }}>
              Palmistry &
              <br />
              <span style={{ color: '#f0c060' }}>Astrology Theme Guidance</span>
            </h1>

            <div className="divider-row">
              <div className="line-l" />
              <span style={{ color: '#c48a14', fontSize: '18px' }}>✦</span>
              <div className="line-r" />
            </div>

            <p style={{
              marginTop: '20px', color: 'rgba(253,248,240,0.75)',
              fontSize: '17px', fontFamily: '"Lato", sans-serif',
              fontWeight: '300', letterSpacing: '0.5px',
              maxWidth: '540px', margin: '20px auto 0', lineHeight: '1.8',
            }}>
              Designing Spaces Aligned with Your Palm & Cosmic Signature
            </p>

            <div className="sub-badges">
              <span className="sub-badge">🖐️ Palm Reading</span>
              <span className="sub-badge">♈ Zodiac Analysis</span>
              <span className="sub-badge">🎨 Theme Guidance</span>
              <span className="sub-badge">🌟 Space Harmony</span>
            </div>
          </div>

          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
            <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '60px' }}>
              <path d="M0,60 C360,0 1080,0 1440,60 L1440,60 L0,60 Z" fill="#fdf8f0" />
            </svg>
          </div>
        </div>

        {/* Intro */}
        <div style={{ maxWidth: '820px', margin: '0 auto', padding: '64px 24px 0', textAlign: 'center' }}>
          <span style={{
            fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase',
            color: '#c48a14', fontWeight: '700', fontFamily: '"Montserrat", sans-serif',
            display: 'block', marginBottom: '16px',
          }}>
            About This Consultation
          </span>
          <p style={{
            fontSize: '16px', color: '#5a3d10', lineHeight: '1.9',
            fontFamily: '"Lato", sans-serif', fontWeight: '300',
          }}>
            This advanced consultation uniquely combines the ancient wisdom of <strong style={{ color: '#825e0a', fontWeight: '700' }}>Palmistry</strong> and <strong style={{ color: '#825e0a', fontWeight: '700' }}>Astrology</strong> to create deeply personalised guidance for your living and working spaces. Your palm reveals your innate nature, life patterns and energy blueprint, while your astrological sign uncovers the planetary elements and cosmic forces that influence your environment. Together, they form a powerful foundation for designing a space that truly resonates with <strong style={{ color: '#825e0a' }}>who you are</strong>.
          </p>
        </div>

        {/* Palm Lines Strip — light bg */}
        <div ref={linesRef} className="palm-lines-strip">
          <div style={{ textAlign: 'center' }}>
            <span style={{
              fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase',
              color: '#c48a14', fontWeight: '700', fontFamily: '"Montserrat", sans-serif',
            }}>
              Palmistry Insights
            </span>
            <h2 style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 'clamp(22px, 3vw, 34px)',
              color: '#2c1a00', fontWeight: '700', marginTop: '8px',
            }}>
              The Four Major Lines We Read
            </h2>
            <div className="divider-row" style={{ marginTop: '12px' }}>
              <div className="line-l" />
              <span style={{ color: '#c48a14', fontSize: '16px' }}>✦</span>
              <div className="line-r" />
            </div>
          </div>

          <div className="palm-lines-grid">
            {palmLines.map((line, i) => (
              <div
                key={i}
                className="palm-line-card"
                style={{
                  opacity: linesVisible ? 1 : 0,
                  transform: linesVisible ? 'translateY(0)' : 'translateY(30px)',
                  transition: `opacity 0.6s ease ${i * 0.12}s, transform 0.6s ease ${i * 0.12}s`,
                }}
              >
                <div style={{ fontSize: '30px', marginBottom: '14px' }}>{line.icon}</div>
                <h4 style={{
                  fontFamily: '"Playfair Display", serif',
                  fontSize: '17px', color: '#2c1a00',
                  fontWeight: '700', marginBottom: '10px',
                }}>
                  {line.name}
                </h4>
                <p style={{
                  fontSize: '13px', color: '#5a3d10',
                  fontFamily: '"Lato", sans-serif', lineHeight: '1.7',
                }}>
                  {line.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Zodiac Elements Strip — dark */}
        <div ref={elementsRef} className="dark-strip">
          <div style={{ textAlign: 'center' }}>
            <span style={{
              fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase',
              color: '#c48a14', fontWeight: '700', fontFamily: '"Montserrat", sans-serif',
            }}>
              Astrological Elements
            </span>
            <h2 style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 'clamp(22px, 3vw, 34px)',
              color: '#fdf8f0', fontWeight: '700', marginTop: '8px',
            }}>
              Your Sign Shapes Your Space
            </h2>
            <div className="divider-row" style={{ marginTop: '12px' }}>
              <div style={{ height: '1px', width: '60px', background: 'linear-gradient(to right, transparent, #c48a1488, #c48a14)' }} />
              <span style={{ color: '#c48a14', fontSize: '16px' }}>✦</span>
              <div style={{ height: '1px', width: '60px', background: 'linear-gradient(to left, transparent, #c48a1488, #c48a14)' }} />
            </div>
            <p style={{
              marginTop: '16px', color: 'rgba(253,248,240,0.6)',
              fontSize: '15px', fontFamily: '"Lato", sans-serif',
              maxWidth: '540px', margin: '16px auto 0', lineHeight: '1.8',
            }}>
              Each zodiac element carries distinct energies that translate directly into ideal home and workspace themes.
            </p>
          </div>

          <div className="elements-grid">
            {zodiacSigns.map((el, i) => (
              <div
                key={i}
                className="element-card"
                style={{
                  opacity: elementsVisible ? 1 : 0,
                  transform: elementsVisible ? 'translateY(0)' : 'translateY(30px)',
                  transition: `opacity 0.6s ease ${i * 0.12}s, transform 0.6s ease ${i * 0.12}s`,
                  borderTop: `3px solid ${el.color}66`,
                }}
              >
                <div style={{
                  fontSize: '38px', marginBottom: '14px',
                  color: el.color, filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))',
                }}>
                  {el.sign}
                </div>
                <h3 style={{
                  fontFamily: '"Playfair Display", serif',
                  fontSize: '20px', color: '#f0c060',
                  fontWeight: '700', marginBottom: '8px',
                }}>
                  {el.name} Signs
                </h3>
                <p style={{
                  fontSize: '12px', color: `${el.color}bb`,
                  fontFamily: '"Montserrat", sans-serif',
                  fontWeight: '600', letterSpacing: '0.5px',
                  marginBottom: '10px',
                }}>
                  {el.members}
                </p>
                <p style={{
                  fontSize: '13px', color: 'rgba(253,248,240,0.6)',
                  fontFamily: '"Lato", sans-serif', lineHeight: '1.6',
                }}>
                  {el.theme}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Process Steps */}
        <div className="section-heading">
          <span style={{
            fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase',
            color: '#c48a14', fontWeight: '700', fontFamily: '"Montserrat", sans-serif',
          }}>
            Our Process
          </span>
          <h2 style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: 'clamp(24px, 3vw, 36px)',
            color: '#2c1a00', fontWeight: '700', marginTop: '8px',
          }}>
            How We Guide Your Space Transformation
          </h2>
          <div className="divider-row">
            <div className="line-l" />
            <span style={{ color: '#c48a14', fontSize: '16px' }}>✦</span>
            <div className="line-r" />
          </div>
        </div>

        <div className="steps-grid">
          {processSteps.map((step, i) => (
            <ProcessCard key={step.number} step={step} index={i} />
          ))}
        </div>

        {/* CTA */}
        <div className="cta-strip">
          <p style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: 'clamp(22px, 3vw, 32px)',
            color: '#fdf8f0', marginBottom: '8px', fontWeight: '700',
          }}>
            Let Your Palm & Stars Design Your Space
          </p>
          <p style={{
            color: 'rgba(253,248,240,0.65)', fontSize: '15px',
            fontFamily: '"Lato", sans-serif', marginBottom: '32px', letterSpacing: '0.5px',
          }}>
            Book an advanced Palmistry & Astrology consultation for a truly personalised space
          </p>
          <a href="https://wa.me/919122397324?text=Hello%20Tarun%2C%20I%E2%80%99m%20interested%20in%20Palmistry%20%26%20Astrology%20Consultation." className="cta-btn">
            Book a Consultation ✦
          </a>
        </div>

      </section>
    </>
  )
}

export default PalmistryAstro